/** tm/legal/LegalFilterSheet.tsx — 모바일 필터: 「☰ 필터」 버튼 → 아래쪽 시트(1열 필터와 같은 항목). 주제·구분을 여러 번 고른 뒤 「적용」으로 닫는다.
 *  「취소」·바깥·Esc·뒤로 가기는 고친 것을 버리고 닫기. MobileIntro = 처음 온 사람을 위한 한 줄 설명(× 로 닫으면 다시 안 보임). */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { C, T, R } from '../components/ui';
import { btn, MUTED } from './legalTheme';
import { introClosed, closeIntro } from './alertPrefs';

export function FilterButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="lw-f" onClick={onClick} aria-haspopup="dialog" style={{ ...btn(), minHeight: 40, maxWidth: '56vw', gap: 6, fontSize: T.md }}>
      <span aria-hidden="true">☰</span> 필터
      <span style={{ color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {label}</span>
    </button>
  );
}

export function MobileIntro({ userId }: { userId: string }) {
  const [closed, setClosed] = useState(() => introClosed(userId));
  if (closed) return null;
  return (
    <div role="note" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 6px 6px 16px', background: C.accentLt, borderBottom: `1px solid #DBEAFE`, fontSize: T.md, color: '#1E40AF', lineHeight: 1.5 }}>
      <span style={{ flex: 1 }}>주제별 법령·판례·해석·국회 법안·뉴스 모음 · 매일 새벽 4시에 모으고 중요한 것은 🔔 새 소식으로</span>
      <button type="button" className="lw-f" aria-label="설명 숨기기" onClick={() => { closeIntro(userId); setClosed(true); }}
        style={{ border: 0, background: 'transparent', color: '#1E40AF', fontSize: 20, width: 40, height: 40, cursor: 'pointer', flexShrink: 0 }}>×</button>
    </div>
  );
}

export default function LegalFilterSheet({ onClose, onApply, children, extra }: { onClose: () => void; onApply: () => void; children: ReactNode; extra?: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k); box.current?.focus();
    return () => document.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(17,24,39,.45)', display: 'flex', alignItems: 'flex-end' }}>
      <div ref={box} role="dialog" aria-modal="true" aria-label="필터" tabIndex={-1} className="lw-pop" style={{
        width: '100%', maxHeight: '86vh', display: 'flex', flexDirection: 'column', background: C.bg, borderRadius: `${R.lg}px ${R.lg}px 0 0`, outline: 'none',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 8px 4px 16px' }}>
          <b style={{ flex: 1, fontSize: T.lg, color: C.text, padding: '8px 0' }}>필터 <span style={{ fontSize: T.sm, fontWeight: 400, color: MUTED }}>주제와 구분을 고른 뒤 「적용」</span></b>
        </div>
        <div style={{ overflowY: 'auto', padding: '4px 16px 16px' }}>{children}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 16px', borderTop: `1px solid ${C.border}`, background: C.white }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="lw-f" onClick={onClose} style={{ ...btn(), flex: 1, justifyContent: 'center', minHeight: 44, fontSize: T.md }}>취소</button>
            <button type="button" className="lw-f" onClick={onApply} style={{ ...btn('pri'), flex: 2, justifyContent: 'center', minHeight: 44, fontSize: T.lg }}>적용</button>
          </div>
          {extra && <div style={{ display: 'flex', gap: 8 }}>{extra}</div>}
        </div>
      </div>
    </div>
  );
}
